import { FormattedNumber, useIntl } from 'react-intl'

import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import type { TypographyColor } from '@gto/ui/typography'
import { Typography } from '@gto/ui/typography'

import type { AffiliateCommissionSchema } from '@/api/gen'
import { PlanName } from '@/components/plan-name'
import { StatusBadge } from '@/components/status-badge'
import { fluidColumn } from '@/components/table-columns'
import type { CommissionStatus } from '@/utils/commission-status'
import { isCommissionStatus } from '@/utils/commission-status'

type CommissionStatusStyle = { textDecoration?: string; commissionColor?: TypographyColor }

// Keyed by the sealed CommissionStatus set — a status added to (or removed
// from) the chokepoint without a style entry is a compile error here.
const COMMISSION_STATUS_STYLE: Record<CommissionStatus, CommissionStatusStyle> = {
  PAID: { commissionColor: 'primary' },
  REFUNDED: { textDecoration: 'line-through', commissionColor: 'text.disabled' },
  LOCKED: { commissionColor: 'text.secondary' },
  UNLOCKED: { commissionColor: 'text.primary' },
}

// The wire `status` is a loose string; an unknown value (a BE status the FE
// doesn't know yet) renders unstyled rather than crashing the cell.
const styleForRow = (row: AffiliateCommissionSchema): CommissionStatusStyle | undefined =>
  isCommissionStatus(row.status) ? COMMISSION_STATUS_STYLE[row.status] : undefined

type CommissionColumnsOptions = {
  showCustomerEmail?: boolean
}

/**
 * Generic over the row so a caller with a RICHER row (the admin orders view's
 * `AffiliateAdminOrderRowSchema` adds `partner_id`/`partner_name`) gets columns typed to
 * its own row and can prepend its own — no cast at the call site.
 */
export const useCommissionColumns = <R extends AffiliateCommissionSchema>(
  options?: CommissionColumnsOptions,
): ColumnDef<R>[] => {
  const { formatMessage } = useIntl()
  const { showCustomerEmail = false } = options ?? {}

  return createColumns<R>([
    {
      accessorKey: 'created_at',
      header: formatMessage({ id: 'commissions.date' }),
      ...fluidColumn({ min: 150, flex: 1.2 }),
      cell: ({ getValue }) => {
        const date = new Date(String(getValue()))

        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      },
    },
    {
      accessorKey: 'id',
      header: formatMessage({ id: 'commissions.transaction' }),
      ...fluidColumn({ min: 95, flex: 0.7 }),
      cell: ({ getValue }) => (
        <span className="text-[0.75rem]">{String(getValue()).slice(0, 8)}</span>
      ),
    },
    {
      accessorKey: 'plan_name',
      header: formatMessage({ id: 'commissions.plan' }),
      ...fluidColumn({ min: 280, flex: 1.8 }),
      cell: ({ getValue }) => <PlanName value={getValue<string | null | undefined>()} />,
    },
    ...(showCustomerEmail
      ? [
          {
            accessorKey: 'customer_email',
            header: formatMessage({ id: 'commissions.customer' }),
            ...fluidColumn({ min: 160, flex: 1.2 }),
            // Both empties collapse to the dash: `??` alone would render an empty cell for
            // `customer_email: ""` (indistinguishable from a rendering bug), and the old
            // `String(value) || '—'` rendered the literal "null" for a missing one.
            cell: ({ getValue }) => {
              const email = getValue<string | null>()
              const isBlank = email == null || email === ''

              return (
                <Typography
                  noWrap
                  color={isBlank ? 'text.disabled' : undefined}
                  variant="body2"
                >
                  {isBlank ? '—' : email}
                </Typography>
              )
            },
          } satisfies ColumnDef<R>,
        ]
      : []),
    {
      accessorKey: 'gross_amount_usd',
      header: formatMessage({ id: 'commissions.revenue' }),
      ...fluidColumn({ min: 90, flex: 0.8, align: 'right' }),
      cell: ({ getValue, row }) => (
        <Typography
          color="text.secondary"
          style={{ textDecoration: styleForRow(row.original)?.textDecoration }}
          variant="body2"
        >
          <FormattedNumber
            currency="USD"
            style="currency"
            value={Number(getValue())}
          />
        </Typography>
      ),
    },
    {
      accessorKey: 'commission_rate',
      header: formatMessage({ id: 'commissions.rate' }),
      ...fluidColumn({ min: 60, flex: 0.5, align: 'right' }),
      cell: ({ getValue, row }) => (
        <Typography
          color="text.secondary"
          style={{ textDecoration: styleForRow(row.original)?.textDecoration }}
          variant="body2"
        >
          <FormattedNumber
            style="percent"
            value={Number(getValue()) / 100}
          />
        </Typography>
      ),
    },
    {
      accessorKey: 'commission_amount_usd',
      header: formatMessage({ id: 'commissions.commission' }),
      ...fluidColumn({ min: 90, flex: 0.8, align: 'right' }),
      cell: ({ getValue, row }) => {
        const style = styleForRow(row.original)

        return (
          <Typography
            color={style?.commissionColor ?? 'text.primary'}
            style={{ textDecoration: style?.textDecoration }}
            variant="body2"
          >
            <FormattedNumber
              currency="USD"
              style="currency"
              value={Number(getValue())}
            />
          </Typography>
        )
      },
    },
    {
      accessorKey: 'coupon_code',
      header: formatMessage({ id: 'commissions.coupon' }),
      ...fluidColumn({ min: 90, flex: 0.8 }),
      cell: ({ getValue }) => {
        const coupon = getValue<string | null | undefined>()

        // `=== ''` as well as nullish, same as the Customer column: an order with an empty
        // `coupon_code` would otherwise render a blank cell rather than "no coupon".
        return coupon == null || coupon === '' ? (
          <Typography
            color="text.disabled"
            variant="body2"
          >
            —
          </Typography>
        ) : (
          <Typography
            className="text-[0.75rem]"
            color="text.secondary"
            variant="body2"
          >
            {coupon}
          </Typography>
        )
      },
    },
    {
      accessorKey: 'status',
      header: formatMessage({ id: 'commissions.status' }),
      ...fluidColumn({ min: 80, flex: 0.6, align: 'right' }),
      cell: ({ getValue }) => <StatusBadge status={String(getValue())} />,
    },
  ])
}
